import type {
  CompanyProfileRecord,
  CustomerSummaryRecord,
  DocumentProjectSummaryRecord,
  InvoiceCollectionRecord,
  InvoiceDetailRecord,
  InvoiceRecord,
  InvoiceStatus
} from '@shared/types';
import { formatInvoiceReference } from '@shared/utils/invoices';

type SortDirection = 'asc' | 'desc';

export interface InvoiceTableRowViewModel {
  id: string;
  reference: string;
  statusLabel: string;
  statusTone: 'neutral' | 'primary' | 'warning' | 'success' | 'info';
  customerName: string;
  issuerName: string;
  dueLabel: string;
  issueLabel: string;
  totalLabel: string;
}

export interface InvoiceSelectionItemViewModel {
  id: string;
  label: string;
  subtitle: string | null;
}

export interface InvoiceEditorViewModel {
  title: string;
  statusLabel: string;
  statusTone: InvoiceTableRowViewModel['statusTone'];
  customerName: string;
  projectName: string;
  issuerName: string;
  issueDateLabel: string;
  dueDateLabel: string;
  subtotalLabel: string;
  taxLabel: string;
  grandTotalLabel: string;
  syncLabel: string;
  exportedLabel: string;
  syncedLabel: string;
}

function getCustomer(summaryList: CustomerSummaryRecord[], customerId: string) {
  return summaryList.find((customer) => customer.id === customerId) ?? null;
}

function getProject(summaryList: DocumentProjectSummaryRecord[], projectId: string | null) {
  return projectId ? summaryList.find((project) => project.id === projectId) ?? null : null;
}

function getIssuer(summaryList: CompanyProfileRecord[], issuerCompanyProfileId: string) {
  return summaryList.find((profile) => profile.id === issuerCompanyProfileId) ?? null;
}

function invoiceStatusTone(status: InvoiceStatus): InvoiceTableRowViewModel['statusTone'] {
  switch (status) {
    case 'paid':
      return 'success';
    case 'issued':
    case 'sent':
      return 'primary';
    case 'partially_paid':
      return 'info';
    case 'overdue':
      return 'warning';
    default:
      return 'neutral';
  }
}

function formatShortDate(locale: string, value: string | null, fallback: string): string {
  if (!value) {
    return fallback;
  }

  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }).format(new Date(value));
}

function formatCurrency(locale: string, value: number, currency: string): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0
  }).format(value);
}

function withDirection(value: number, direction: SortDirection): number {
  return direction === 'asc' ? value : value * -1;
}

function sortInvoices(invoices: InvoiceRecord[], sort: 'issueDate' | 'dueDate' | 'number', direction: SortDirection): InvoiceRecord[] {
  const nextInvoices = [...invoices];

  switch (sort) {
    case 'dueDate':
      return nextInvoices.sort((left, right) => {
        if (!left.dueDate && !right.dueDate) {
          return 0;
        }
        if (!left.dueDate) {
          return 1;
        }
        if (!right.dueDate) {
          return -1;
        }

        return withDirection(new Date(left.dueDate).getTime() - new Date(right.dueDate).getTime(), direction);
      });
    case 'number':
      return nextInvoices.sort((left, right) =>
        withDirection(formatInvoiceReference(left.series, left.number).localeCompare(formatInvoiceReference(right.series, right.number)), direction)
      );
    default:
      return nextInvoices.sort((left, right) => withDirection(new Date(left.issueDate).getTime() - new Date(right.issueDate).getTime(), direction));
  }
}

export function selectInvoicesSummary(collection: InvoiceCollectionRecord) {
  return {
    activeCount: collection.invoices.filter((invoice) => invoice.status !== 'archived').length,
    archivedCount: collection.invoices.filter((invoice) => invoice.status === 'archived').length
  };
}

export function selectInvoiceRows(
  collection: InvoiceCollectionRecord,
  options: {
    locale: string;
    statusFilter: 'all' | InvoiceStatus;
    customerFilter: 'all' | string;
    issuerFilter: 'all' | string;
    dueFilter: 'all' | 'open' | 'overdue' | 'paid';
    sort: 'issueDate' | 'dueDate' | 'number';
    sortDirection?: SortDirection | undefined;
    t: (key: string, values?: Record<string, string | number>) => string;
  }
): InvoiceTableRowViewModel[] {
  return sortInvoices(
    collection.invoices.filter((invoice) => {
      const matchesStatus = options.statusFilter === 'all' || invoice.status === options.statusFilter;
      const matchesCustomer = options.customerFilter === 'all' || invoice.customerId === options.customerFilter;
      const matchesIssuer = options.issuerFilter === 'all' || invoice.issuerCompanyProfileId === options.issuerFilter;
      const matchesDue =
        options.dueFilter === 'all' ||
        (options.dueFilter === 'paid' && invoice.status === 'paid') ||
        (options.dueFilter === 'overdue' && invoice.status === 'overdue') ||
        (options.dueFilter === 'open' &&
          (invoice.status === 'issued' || invoice.status === 'sent' || invoice.status === 'partially_paid'));

      return matchesStatus && matchesCustomer && matchesIssuer && matchesDue;
    }),
    options.sort,
    options.sortDirection ?? (options.sort === 'number' ? 'asc' : 'desc')
  ).map((invoice) => {
    const customer = getCustomer(collection.customerSummaries, invoice.customerId);
    const issuer = getIssuer(collection.companyProfiles, invoice.issuerCompanyProfileId);

    return {
      id: invoice.id,
      reference: formatInvoiceReference(invoice.series, invoice.number),
      statusLabel: options.t(`enums.invoiceStatus.${invoice.status}`),
      statusTone: invoiceStatusTone(invoice.status),
      customerName: customer?.name ?? invoice.customerSnapshot.displayName,
      issuerName: issuer?.name ?? invoice.issuerSnapshot.displayName,
      dueLabel: invoice.dueDate
        ? options.t('invoices.table.dueDate', {
            date: formatShortDate(options.locale, invoice.dueDate, options.t('common.notSet'))
          })
        : options.t('common.noDeadline'),
      issueLabel: formatShortDate(options.locale, invoice.issueDate, options.t('common.notSet')),
      totalLabel: formatCurrency(options.locale, invoice.grandTotal, invoice.currency)
    };
  });
}

export function selectInvoiceCustomerOptions(collection: InvoiceCollectionRecord, t: (key: string) => string) {
  return [
    { value: 'all', label: t('invoices.filters.allCustomers') },
    ...collection.customerSummaries.map((customer) => ({
      value: customer.id,
      label: customer.name
    }))
  ];
}

export function selectInvoiceIssuerOptions(collection: InvoiceCollectionRecord, t: (key: string) => string) {
  return [
    { value: 'all', label: t('invoices.filters.allIssuers') },
    ...collection.companyProfiles.map((profile) => ({
      value: profile.id,
      label: profile.name
    }))
  ];
}

export function selectInvoiceSelectionItems(collection: InvoiceCollectionRecord): InvoiceSelectionItemViewModel[] {
  return collection.invoices.map((invoice) => ({
    id: invoice.id,
    label: formatInvoiceReference(invoice.series, invoice.number),
    subtitle: invoice.customerSnapshot.displayName
  }));
}

export function selectInvoiceEditorModel(
  detail: InvoiceDetailRecord,
  options: {
    locale: string;
    t: (key: string, values?: Record<string, string | number>) => string;
  }
): InvoiceEditorViewModel {
  const customer = getCustomer(detail.customerSummaries, detail.invoice.customerId);
  const project = getProject(detail.projectSummaries, detail.invoice.projectId);
  const issuer = getIssuer(detail.companyProfiles, detail.invoice.issuerCompanyProfileId);

  return {
    title: formatInvoiceReference(detail.invoice.series, detail.invoice.number),
    statusLabel: options.t(`enums.invoiceStatus.${detail.invoice.status}`),
    statusTone: invoiceStatusTone(detail.invoice.status),
    customerName: customer?.name ?? detail.invoice.customerSnapshot.displayName,
    projectName: project?.name ?? options.t('common.notSet'),
    issuerName: issuer?.name ?? detail.invoice.issuerSnapshot.displayName,
    issueDateLabel: formatShortDate(options.locale, detail.invoice.issueDate, options.t('common.notSet')),
    dueDateLabel: formatShortDate(options.locale, detail.invoice.dueDate, options.t('common.notSet')),
    subtotalLabel: formatCurrency(options.locale, detail.invoice.subtotal, detail.invoice.currency),
    taxLabel: formatCurrency(options.locale, detail.invoice.taxTotal, detail.invoice.currency),
    grandTotalLabel: formatCurrency(options.locale, detail.invoice.grandTotal, detail.invoice.currency),
    syncLabel: options.t(`enums.invoiceSyncStatus.${detail.invoice.externalSyncStatus}`),
    exportedLabel: formatShortDate(options.locale, detail.invoice.exportedAt, options.t('common.notSet')),
    syncedLabel: formatShortDate(options.locale, detail.invoice.externalSyncedAt, options.t('common.notSet'))
  };
}
